import { Menu as MuiMenu, MenuItem, Typography } from '@mui/material';
import React, { memo, useCallback } from 'react';

import { CompanyStatus } from '~/types/models';

interface Props {
  statuses: CompanyStatus[];
  anchorEl: HTMLElement | null;
  onClose: (event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => void;
  onSelect: (status: CompanyStatus) => void;
  onDelete?: () => void;
}

const Component: React.FC<Props> = ({ statuses, anchorEl, onClose, onSelect, onDelete }) => {
  const handleSelect = useCallback(
    (event: React.MouseEvent<HTMLLIElement>, status: CompanyStatus) => {
      onSelect(status);

      onClose(event);
    },
    [onClose, onSelect],
  );

  const getStatusName = useCallback((status: CompanyStatus) => {
    if (status === 'draft') {
      return 'Draft';
    }

    if (status === 'closed') {
      return 'Accepted';
    }

    if (status === 'in_progress') {
      return 'In Progress';
    }

    if (status === 'rejected') {
      return 'Rejected';
    }

    return null;
  }, []);

  const getStatusColor = useCallback((status: CompanyStatus) => {
    if (status === 'draft') {
      return 'var(--gray-2-color)';
    }

    if (status === 'closed') {
      return 'var(--main-1-color)';
    }

    if (status === 'in_progress') {
      return 'var(--success-color)';
    }

    if (status === 'rejected') {
      return 'var(--error-color)';
    }

    return undefined;
  }, []);

  return (
    <MuiMenu
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      open={Boolean(anchorEl)}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      onClose={onClose}
    >
      {statuses.map(status => (
        <MenuItem key={status} onClick={event => handleSelect(event, status)}>
          <Typography color="var(--text-2-color)" display="flex" lineHeight="20px" variant="body1">
            Move to
            <Typography
              color={getStatusColor(status)}
              component="span"
              lineHeight="20px"
              ml="5px"
              variant="body1"
            >
              {getStatusName(status)}
            </Typography>
          </Typography>
        </MenuItem>
      ))}
      {onDelete && (
        <MenuItem onClick={onDelete}>
          <Typography color="var(--text-2-color)" lineHeight="20px" variant="body1">
            Delete
          </Typography>
        </MenuItem>
      )}
    </MuiMenu>
  );
};

export const Menu = memo(Component);
